/**
 * Toast notification context.
 *
 * Replaces the ad-hoc `setError(msg)` pattern scattered across pages
 * with a centralized, dismissible, stackable notification system.
 *
 * Usage:
 *
 *   const { showToast } = useToast();
 *   showToast('CRF saved', 'success');
 *   showToast('Failed to load', 'error');
 *   showToast('Heads-up: edit checks pending', 'warning');
 *   showToast('Generation queued', 'info');
 *
 * Non-error toasts auto-dismiss after `DEFAULT_TIMEOUT_MS`. Error toasts
 * are PERSISTENT — they stay until the user dismisses them, so a failure
 * message and its reference code aren't lost before the user reads it.
 * A manual close button is rendered on every toast.
 *
 * The provider must wrap any tree that wants to call `useToast()` —
 * see main.tsx for the app-level wiring.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Info,
  RotateCcw,
  X,
} from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * Optional inline action button rendered on a toast — used for things
 * like "Undo" after a destructive action. Clicking the button invokes
 * `onClick` and dismisses the toast.
 */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: number;
  message: string;
  type: ToastType;
  action?: ToastAction;
}

export interface ShowToastOptions {
  /**
   * Override the auto-dismiss timer in milliseconds. `0` disables
   * auto-dismiss entirely. When omitted, defaults pick up the
   * type-dependent timeout (errors/warnings stay longer; toasts with
   * an action stay even longer to give the user time to react).
   */
  durationMs?: number;
  /** Optional inline action button (e.g. "Undo"). */
  action?: ToastAction;
}

interface ToastContextValue {
  /**
   * Display a toast. Auto-dismisses after a type- and action-dependent
   * timeout unless `options.durationMs: 0` is passed.
   */
  /**
   * Returns the toast's id, so a caller can dismiss the toast IT raised once
   * the condition is gone. Error toasts are persistent on purpose (see
   * DEFAULT_TIMEOUT_MS below); without an id, nothing could ever clear one, and
   * a fixed problem stayed on screen looking unfixed — which is exactly what
   * happened on 2026-08-26 with a Short Name refusal that had been resolved
   * minutes earlier.
   */
  showToast: (
    message: string,
    type?: ToastType,
    options?: ShowToastOptions,
  ) => number;
  /** Imperatively dismiss a toast (used by the close button). */
  dismissToast: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const DEFAULT_TIMEOUT_MS: Record<ToastType, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  // Errors are PERSISTENT (0 = no auto-dismiss): a generation failure often
  // carries a specific reason and a support reference code the user needs time
  // to read and copy, so it stays until the user dismisses it with the close
  // button. Callers can still pass an explicit `durationMs` to override.
  error: 0,
};

/**
 * Toasts with an action button stay longer than the type default so
 * the user has time to react before the toast (and its undo affordance)
 * disappears.
 */
const ACTION_TIMEOUT_MS = 6000;

/**
 * Soft cap on how many toasts can stack at once. When rapid-fire
 * actions (e.g. resolving 30 duplicate variables in CRFBuilder) emit
 * many toasts in a row, older ones get auto-dropped so the stack
 * doesn't cover the buttons users want to click next.
 */
const MAX_TOASTS = 5;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Monotonic id counter; refs avoid re-render churn.
  const nextIdRef = useRef(0);
  // Active timers, keyed by toast id, so dismiss can cancel cleanly.
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismissToast = useCallback((id: number): void => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    (
      message: string,
      type: ToastType = 'info',
      options?: ShowToastOptions,
    ): number => {
      const id = nextIdRef.current++;
      setToasts((prev) => {
        const next = [...prev, { id, message, type, action: options?.action }];
        // Drop oldest when over the cap so the stack can't hide buttons.
        return next.length > MAX_TOASTS
          ? next.slice(next.length - MAX_TOASTS)
          : next;
      });

      const defaultTimeout = options?.action
        ? ACTION_TIMEOUT_MS
        : DEFAULT_TIMEOUT_MS[type];
      const timeout = options?.durationMs ?? defaultTimeout;
      if (timeout > 0) {
        const timer = setTimeout(() => dismissToast(id), timeout);
        timersRef.current.set(id, timer);
      }
      return id;
    },
    [dismissToast],
  );

  // Clean up any pending timers on unmount so a toast firing after
  // teardown doesn't try to setState on a dead provider.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      <ToastDisplay toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// ──────────────────────────────────────────────────────────────────────
// Display
// ──────────────────────────────────────────────────────────────────────

interface ToastDisplayProps {
  toasts: Toast[];
  onDismiss: (id: number) => void;
}

/**
 * Stack of toasts pinned to the bottom-left. Each toast is a small
 * card with a left accent bar colored by type, the matching lucide
 * icon, the message, and a manual dismiss button.
 *
 * `pointer-events-none` on the outer container lets clicks fall
 * through to the page; the inner cards re-enable pointer events so
 * the dismiss button is still clickable.
 */
function ToastDisplay({ toasts, onDismiss }: ToastDisplayProps) {
  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed bottom-4 left-4 z-50 flex flex-col gap-2 w-full max-w-sm"
    >
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

interface ToastCardProps {
  toast: Toast;
  onDismiss: (id: number) => void;
}

function ToastCard({ toast, onDismiss }: ToastCardProps) {
  const styles = TYPE_STYLES[toast.type];
  const Icon = styles.icon;

  return (
    <div
      role={toast.type === 'error' || toast.type === 'warning' ? 'alert' : 'status'}
      className={`pointer-events-auto flex items-stretch gap-3 bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden ${styles.accent}`}
    >
      <div className={`flex-shrink-0 flex items-center justify-center w-10 self-stretch ${styles.iconBg}`}>
        <Icon className={`w-5 h-5 ${styles.iconColor}`} />
      </div>
      <div className="flex-1 py-3 min-w-0 flex items-center">
        <p className="text-sm text-slate-900 break-words">{toast.message}</p>
      </div>
      {toast.action && (
        <button
          onClick={() => {
            toast.action!.onClick();
            onDismiss(toast.id);
          }}
          className="flex-shrink-0 self-center inline-flex items-center gap-1 px-3 py-1.5 mr-1 rounded bg-slate-900 text-white text-xs font-semibold uppercase tracking-wide hover:bg-slate-800 transition-colors"
        >
          <RotateCcw className="w-3 h-3" />
          {toast.action.label}
        </button>
      )}
      <button
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
        className="flex-shrink-0 self-start p-2 text-slate-400 hover:text-slate-600 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

const TYPE_STYLES: Record<
  ToastType,
  {
    icon: typeof CheckCircle2;
    iconColor: string;
    iconBg: string;
    accent: string;
  }
> = {
  success: {
    icon: CheckCircle2,
    iconColor: 'text-green-600',
    iconBg: 'bg-green-50',
    accent: 'border-l-4 border-l-green-500',
  },
  error: {
    icon: AlertCircle,
    iconColor: 'text-red-600',
    iconBg: 'bg-red-50',
    accent: 'border-l-4 border-l-red-500',
  },
  warning: {
    icon: AlertTriangle,
    iconColor: 'text-amber-600',
    iconBg: 'bg-amber-50',
    accent: 'border-l-4 border-l-amber-500',
  },
  info: {
    icon: Info,
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-50',
    accent: 'border-l-4 border-l-blue-500',
  },
};
